import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromProducts from '@appStore/products/reducers/products.reducer';
import * as fromAgreements from '@appStore/products/reducers/agreement.reducer';
import * as fromServiceAgreements from '@appStore/products/reducers/service-agreements.reducer';
import * as fromRoot from '../../../reducers';
import { MeteringPointsState } from '@appStore/metering-points/reducers';
import { CustomersState } from '@appStore/customers';
import { ServiceAgreementInformation } from '@model';
import { sortByProperty } from '@utils/sx-utilities';
import { ServiceAgreementStatusType } from '@enums';
import { ConfigurationState } from '@appStore/configuration';

export interface ProductsState {
  products: fromProducts.State;
  agreements: fromAgreements.State;
  serviceAgreements: fromServiceAgreements.State;
}

export interface State extends fromRoot.State {
  products: ProductsState;
}

export const reducers: ActionReducerMap<ProductsState> = {
  products: fromProducts.reducer,
  agreements: fromAgreements.reducer,
  serviceAgreements: fromServiceAgreements.reducer
};

export const getState = createFeatureSelector<ProductsState>('products');

export const getProductsState = createSelector(
  getState,
  (state: ProductsState) => state.products
);

// Products
export const getTexts = createSelector(
  getProductsState,
  fromProducts.getTexts
);

export const getAllProducts = createSelector(
  getProductsState,
  fromProducts.getAll
);

export const getProductsLoaded = createSelector(
  getProductsState,
  fromProducts.getLoaded
);

export const getProductsLoading = createSelector(
  getProductsState,
  fromProducts.getLoading
);

export const getIsFi2XmlRegisteredOnCustomerLoaded = createSelector(
  getProductsState,
  fromProducts.getIsRegisteredLoaded
);

export const getIsFi2XmlRegisteredOnCustomer = createSelector(
  getProductsState,
  fromProducts.getIsRegistered
);

// Agreements
export const getAgreementState = createSelector(
  getState,
  (state: ProductsState) => state.agreements
);

export const getProductAgreementCreated = createSelector(
  getAgreementState,
  fromAgreements.getProductAgreementCreated
);

export const getProductAgreementCreatedId = createSelector(
  getAgreementState,
  fromAgreements.getProductAgreementCreateId
);

export const getActiveAgreementLoaded = createSelector(
  getAgreementState,
  fromAgreements.getActiveAgreementLinkLoaded
);

export const getActiveAgreementLink = createSelector(
  getAgreementState,
  fromAgreements.getActiveAgreementLink
);

// Service Agreements
export const getServiceAgreementsState = createSelector(
  getState,
  (state: ProductsState) => state.serviceAgreements
);

export const getServiceAgreementsLoaded = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getServiceAgreementsLoaded
);

export const getServiceAgreements = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getServiceAgreements
);

export const getServiceFilledAgreements = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getServiceAgreements
);

// Combined selectors
const getCustomersFeatureState = createFeatureSelector<CustomersState>('customers');
const getCustomersState = createSelector(
  getCustomersFeatureState,
  state => state.customers
);

const getMeteringPointFeatureState = createFeatureSelector<MeteringPointsState>('meteringpoints');
const getMeteringPointsState = createSelector(
  getMeteringPointFeatureState,
  state => state.meteringPoints
);

const getConfigurationFeatureState = createFeatureSelector<ConfigurationState>('configuration');
const getConfigurationState = createSelector(
  getConfigurationFeatureState,
  state => state.configuration
);

// ServiceAgreement 

export const getServiceAgreementsFilter = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getServiceAgreementsFilter
);

export const getSelectedServiceAgreement = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getSelectedServiceAgreement
);

export const getSelectedTabIndex = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getSelectedTabIndexServiceAgreements
);
export const getServiceAgreementsFilterSettings = createSelector(
  getServiceAgreementsState,
  fromServiceAgreements.getServiceAgreementsFilterSettings
);

export const getFilledServiceAgreementInformationsLoaded = createSelector(
  getConfigurationState,
  getCustomersState,
  getMeteringPointsState,
  getServiceAgreementsState,
  (configState, customerState, mpState, state) => {
    return state.loaded && mpState.loaded && customerState.loaded && configState.apiConfigurationLoaded;
  }
);

export const getFilledServiceAgreementInformations = createSelector(
  getConfigurationState,
  getCustomersState,
  getMeteringPointsState,
  getServiceAgreementsState,
  (configState, customerState, mpState, state) => {
    if (!state.loaded || !customerState.loaded || !mpState.loaded || !configState.apiConfigurationLoaded) {
      return;
    }

    if(state.data.length === 0) {
      return [];
    }

    let serviceAgreementInformations = [] as ServiceAgreementInformation[];
    let functionControlServiceAgreementCodes = configState.apiConfiguration?.functionControlServiceAgreementCodes.split(';');
    let intelligyServiceAgreementCodes = configState.apiConfiguration?.intelligyServiceAgreementCodes.split(';');

    state.data.forEach(
      sa => (serviceAgreementInformations = [...serviceAgreementInformations, ...sa.serviceAgreementInformation.map(sai => 
        ({ ...sai, 
          customerId: sa.customerId 
        }))
      ])
    );

    if(serviceAgreementInformations.length === 0) {
      return [];
    }

    return sortByProperty(serviceAgreementInformations.map(sai => {

      let _meteringPoint = mpState.data.find(mp => mp.id === sai.meteringPointId);
      return {
        ...sai,
        customerName: customerState.data.find(c => c.id === sai.customerId)?.name,
        meteringPointName: _meteringPoint ? _meteringPoint?.name : '',
        hasIntelligy: _meteringPoint ? _meteringPoint?.hasIntelligy && intelligyServiceAgreementCodes.some(code => sai?.serviceId.includes(code)) : false,
        hasFunctionControl: _meteringPoint ? functionControlServiceAgreementCodes.some(code => sai?.serviceId.includes(code)) : false
      };
    }), 'agreementFrom', 'desc');
  }
);
